import { Navigate, useLocation } from "react-router-dom";
import { ReactNode } from "react";
import { jwtDecode } from "jwt-decode";
import { invalidateClientSession } from "@/shared/utils/authSession.util";
import { useAuth } from "@/hooks/useAuth";
import { isClusterPortalRole } from "@/shared/utils/clusterPortalRole.util";
import { AuthRouteFallback } from "@/routes/RouteFallback";
import { isCompanyAppPortalRole } from "@/shared/utils/expoVisitorRole.util";

type AdminProtectedRouteProps = {
  children: ReactNode;
};

type DecodedToken = {
  id: string;
  email: string;
  role: string;
  exp: number;
  iat: number;
};

export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const location = useLocation();
  const { token, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <AuthRouteFallback sidebarBg="#ffffff" />;
  }

  if (!isAuthenticated || !token) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  try {
    const decoded = jwtDecode<DecodedToken>(token);

    // Do not wipe session on JWT exp here — useAuth + silent refresh renew access.
    // Role checks only for correct portal.

    if (isCompanyAppPortalRole(decoded.role)) {
      return <Navigate to="/profile" replace />;
    }

    if (decoded.role === "FACILITATOR") {
      return <Navigate to="/facilitator/dashboard" replace />;
    }

    if (isClusterPortalRole(decoded.role)) {
      return <Navigate to="/ecosystem-head/dashboard" replace />;
    }

    if (decoded.role === "EXPO_HEAD") {
      return <Navigate to="/expo-head/dashboard" replace />;
    }

    if (decoded.role === "EXPERT") {
      return <Navigate to="/expert/dashboard" replace />;
    }

    if (decoded.role !== "ADMIN" && decoded.role !== "SUB_ADMIN") {
      invalidateClientSession();
      return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }

    return <>{children}</>;
  } catch {
    invalidateClientSession();
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
}
